import axiosInstance from './AxiosInstance';

/**
 * Staff Management Service
 * All API calls for staff management including CRUD operations
 * for staff, addresses, contacts, employments, and documents
 */

// ==================== STAFF CRUD ====================

/**
 * Create a new staff member
 * @param {Object} staffData - Staff data
 * @returns {Promise}
 */
export const createStaff = (staffData) => {
  return axiosInstance.post('/staff', staffData);
};

/**
 * Update staff member
 * @param {number} staffId - Staff ID
 * @param {Object} staffData - Staff data
 * @returns {Promise}
 */
export const updateStaff = (staffId, staffData) => {
  return axiosInstance.put(`/staff/${staffId}`, staffData);
};

/**
 * Get staff by ID
 * @param {number} staffId - Staff ID
 * @returns {Promise}
 */
export const getStaffById = (staffId) => {
  return axiosInstance.get(`/staff/${staffId}`);
};

/**
 * List staff by corporate with pagination and filters
 * @param {number} corporateId - Corporate ID
 * @param {Object} params - Query parameters (page, limit, search, status, branch_id, department_id, sub_department_id)
 * @returns {Promise}
 */
export const formatStaffName = (staff) => {
  const name = [staff?.first_name, staff?.middle_name, staff?.last_name].filter(Boolean).join(' ').trim();
  return name || staff?.employee_code || (staff?.id ? `Staff #${staff.id}` : '');
};

export const listReportsToOptions = () => {
  return axiosInstance.get('/auth/corporateuser/reports-to-options');
};

export const listStaffByCorporate = (corporateId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.branch_id) queryParams.append('branch_id', params.branch_id);
  if (params.department_id) queryParams.append('department_id', params.department_id);
  if (params.sub_department_id) queryParams.append('sub_department_id', params.sub_department_id);
  if (params.linked_users) queryParams.append('linked_users', params.linked_users);
  
  const queryString = queryParams.toString();
  return axiosInstance.get(`/staff/corporate/${corporateId}${queryString ? '?' + queryString : ''}`);
};

/**
 * Activate staff member
 * @param {number} staffId - Staff ID
 * @returns {Promise}
 */
export const activateStaff = (staffId) => {
  return axiosInstance.patch(`/staff/${staffId}/activate`);
};

/**
 * Deactivate staff member
 * @param {number} staffId - Staff ID
 * @returns {Promise}
 */
export const deactivateStaff = (staffId) => {
  return axiosInstance.patch(`/staff/${staffId}/deactivate`);
};

/**
 * Get staff statistics
 * @param {number} corporateId - Corporate ID
 * @returns {Promise}
 */
export const getStaffStatistics = (corporateId) => {
  return axiosInstance.get(`/staff/corporate/${corporateId}/stats`);
};

// ==================== STAFF ADDRESSES ====================

/**
 * Create staff address
 * @param {number} staffId - Staff ID
 * @param {Object} addressData - Address data
 * @returns {Promise}
 */
export const createStaffAddress = (staffId, addressData) => {
  return axiosInstance.post(`/staff/${staffId}/addresses`, addressData);
};

/**
 * Update staff address
 * @param {number} staffId - Staff ID
 * @param {number} addressId - Address ID
 * @param {Object} addressData - Address data
 * @returns {Promise}
 */
export const updateStaffAddress = (staffId, addressId, addressData) => {
  return axiosInstance.put(`/staff/${staffId}/addresses/${addressId}`, addressData);
};

/**
 * Delete staff address
 * @param {number} staffId - Staff ID
 * @param {number} addressId - Address ID
 * @returns {Promise}
 */
export const deleteStaffAddress = (staffId, addressId) => {
  return axiosInstance.delete(`/staff/${staffId}/addresses/${addressId}`);
};

// ==================== STAFF CONTACTS ====================

/**
 * Create staff contact
 * @param {number} staffId - Staff ID
 * @param {Object} contactData - Contact data
 * @returns {Promise}
 */
export const createStaffContact = (staffId, contactData) => {
  return axiosInstance.post(`/staff/${staffId}/contacts`, contactData);
};

/**
 * Update staff contact
 * @param {number} staffId - Staff ID
 * @param {number} contactId - Contact ID
 * @param {Object} contactData - Contact data
 * @returns {Promise}
 */
export const updateStaffContact = (staffId, contactId, contactData) => {
  return axiosInstance.put(`/staff/${staffId}/contacts/${contactId}`, contactData);
};

/**
 * Delete staff contact
 * @param {number} staffId - Staff ID
 * @param {number} contactId - Contact ID
 * @returns {Promise}
 */
export const deleteStaffContact = (staffId, contactId) => {
  return axiosInstance.delete(`/staff/${staffId}/contacts/${contactId}`);
};

// ==================== STAFF EMPLOYMENTS ====================

/**
 * Create staff employment
 * @param {number} staffId - Staff ID
 * @param {Object} employmentData - Employment data
 * @returns {Promise}
 */
export const createStaffEmployment = (staffId, employmentData) => {
  return axiosInstance.post(`/staff/${staffId}/employments`, employmentData);
};

/**
 * Update staff employment
 * @param {number} staffId - Staff ID
 * @param {number} employmentId - Employment ID
 * @param {Object} employmentData - Employment data
 * @returns {Promise}
 */
export const updateStaffEmployment = (staffId, employmentId, employmentData) => {
  return axiosInstance.put(`/staff/${staffId}/employments/${employmentId}`, employmentData);
};

/**
 * Delete staff employment
 * @param {number} staffId - Staff ID
 * @param {number} employmentId - Employment ID
 * @returns {Promise}
 */
export const deleteStaffEmployment = (staffId, employmentId) => {
  return axiosInstance.delete(`/staff/${staffId}/employments/${employmentId}`);
};

// ==================== STAFF DOCUMENTS ====================

/**
 * Create staff document
 * @param {number} staffId - Staff ID
 * @param {FormData|Object} documentData - Document data (FormData for file upload or Object for JSON)
 * @returns {Promise}
 */
export const createStaffDocument = (staffId, documentData) => {
  const isFormData = documentData instanceof FormData;
  const config = isFormData ? {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  } : {};
  return axiosInstance.post(`/staff/${staffId}/documents`, documentData, config);
};

/**
 * Update staff document
 * @param {number} staffId - Staff ID
 * @param {number} documentId - Document ID
 * @param {FormData|Object} documentData - Document data (FormData for file upload or Object for JSON)
 * @returns {Promise}
 */
export const updateStaffDocument = (staffId, documentId, documentData) => {
  const isFormData = documentData instanceof FormData;
  const config = isFormData ? {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  } : {};
  return axiosInstance.put(`/staff/${staffId}/documents/${documentId}`, documentData, config);
};

/**
 * Delete staff document
 * @param {number} staffId - Staff ID
 * @param {number} documentId - Document ID
 * @returns {Promise}
 */
export const deleteStaffDocument = (staffId, documentId) => {
  return axiosInstance.delete(`/staff/${staffId}/documents/${documentId}`);
};
